'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, Check, AlertTriangle, Download } from 'lucide-react';
import { proverit, type Otchyot, type Nahodka } from '@/lib/humanizer/proverka';
import { PoleTeksta } from '@/components/aisearch/pole-teksta';
import { useChernovikFormy } from '@/lib/ispolzovat-chernovik';
import { zagruzitSlovar, slovarGotov, VES_SLOVARYA_MB } from '@/lib/humanizer/slovar';
import type { SlovarChastejRechi } from '@/lib/humanizer/proverka';

/**
 * Проверка текста на признаки нейросети.
 *
 * Всё считается в браузере. Текст никуда не уходит, проверка ничего не
 * стоит и не тратит лимит, поэтому здесь нет ни одного запроса к нашему
 * API. Это же снимает главное возражение: человек несёт сюда черновики,
 * письма и дипломы, и отправлять их на чужой сервер ради оценки он не
 * обязан.
 *
 * Жанр меняет правила: в научной статье «данный» законен, в законе
 * «настоящим уведомляем» тоже. Без выбора жанра диплом получал бы низкую
 * оценку за то, что написан как диплом.
 */

/** Предел ввода. Больше десяти тысяч знаков за раз никто не вычитывает. */
const MAX_ZNAKOV = 20000;

/** Жанры, в которых часть правил отключается. Коды те же, что в правилах. */
const ZHANRY = [
  { kod: '', klyuch: 'zhanr_lyuboj' },
  { kod: 'academic', klyuch: 'zhanr_academic' },
  { kod: 'legal', klyuch: 'zhanr_legal' },
  { kod: 'news', klyuch: 'zhanr_news' },
  { kod: 'fiction', klyuch: 'zhanr_fiction' },
] as const;

/** Цвет полосы по оценке: зелёный, жёлтый, красный. */
function cvetPolosy(ocenka: number): string {
  if (ocenka >= 85) return 'text-emerald-500';
  if (ocenka >= 60) return 'text-amber-500';
  return 'text-red-500';
}

export function ProverkaTeksta() {
  const t = useTranslations('ais_proverka');

  // Введённое переживает перезагрузку: сюда несут главы дипломов и
  // статьи, и терять их из-за случайно обновлённой страницы человек не
  // должен. Жанр хранится рядом с текстом — это часть того же решения,
  // и переспрашивать его незачем.
  const [chernovik, setChernovik] = useChernovikFormy('ais-proverka', {
    text: '',
    zhanr: '',
  });
  const { text, zhanr } = chernovik;
  const setText = (next: string) => {
    setChernovik((s) => ({ ...s, text: next }));
  };
  const setZhanr = (next: string) => {
    setChernovik((s) => ({ ...s, zhanr: next }));
  };
  const [otchyot, setOtchyot] = useState<Otchyot | null>(null);
  const [slovar, setSlovar] = useState<SlovarChastejRechi | null>(() => slovarGotov());
  const [gruzitsya, setGruzitsya] = useState(false);
  const [oshibkaSlovarya, setOshibkaSlovarya] = useState(false);

  const proverit_ = useCallback(() => {
    setOtchyot(proverit(text, zhanr === '' ? null : zhanr, slovar ?? undefined));
  }, [text, zhanr, slovar]);

  const vzyatSlovar = useCallback(async () => {
    setGruzitsya(true);
    setOshibkaSlovarya(false);
    try {
      const s = await zagruzitSlovar();
      setSlovar(s);
      // Пересчитываем сразу: человек нажал ради этого пункта, и заставлять
      // его жать «Проверить» второй раз незачем.
      if (text.trim() !== '') setOtchyot(proverit(text, zhanr === '' ? null : zhanr, s));
    } catch {
      setOshibkaSlovarya(true);
    } finally {
      setGruzitsya(false);
    }
  }, [text, zhanr]);

  /** Находки, сложенные по категориям: иначе список выходит на сотню строк. */
  const poKategoriyam = useMemo(() => {
    if (!otchyot) return [];
    const karta = new Map<string, Nahodka[]>();
    for (const n of [...otchyot.zaprety, ...otchyot.markery]) {
      const spisok = karta.get(n.kategoriya) ?? [];
      spisok.push(n);
      karta.set(n.kategoriya, spisok);
    }
    return [...karta.entries()].sort(
      (a, b) =>
        b[1].reduce((n, h) => n + h.kolichestvo, 0) - a[1].reduce((n, h) => n + h.kolichestvo, 0),
    );
  }, [otchyot]);

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6">
      <label className="mb-2 block text-sm font-medium" htmlFor="proverka-text">
        {t('text_label')}
      </label>
      <PoleTeksta
        id="proverka-text"
        value={text}
        onChange={setText}
        rows={10}
        maxLength={MAX_ZNAKOV}
        placeholder={t('text_placeholder')}
        className="px-4"
      />
      {/* Счёт знаков переехал внутрь поля, здесь осталась только
          подсказка: два счётчика рядом читаются как расхождение, а не
          как повтор, и человек начинает искать, который из них верный. */}
      <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('text_hint')}</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium" htmlFor="proverka-zhanr">
            {t('zhanr_label')}
          </label>
          <select
            id="proverka-zhanr"
            value={zhanr}
            onChange={(e) => {
              setZhanr(e.target.value);
            }}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--color-accent-500)]"
          >
            {ZHANRY.map((z) => (
              <option key={z.kod === '' ? 'any' : z.kod} value={z.kod}>
                {t(z.klyuch)}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{t('zhanr_hint')}</p>
        </div>

        <div className="flex items-end">
          <button
            type="button"
            onClick={proverit_}
            disabled={text.trim() === ''}
            className="w-full rounded-xl bg-[var(--color-accent-500)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--color-accent-400)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('run')}
          </button>
        </div>
      </div>

      {otchyot !== null && (
        <div className="mt-8 border-t border-[var(--color-border)] pt-6">
          <div className="mb-6 flex items-baseline gap-4">
            <span className={`text-5xl font-bold tabular-nums ${cvetPolosy(otchyot.ocenka)}`}>
              {otchyot.ocenka}
            </span>
            <div>
              <p className="font-semibold">{t(`polosa_${otchyot.polosa}`)}</p>
              <p className="text-xs text-[var(--color-text-muted)]">
                {t('slov', { n: otchyot.slov })}
              </p>
            </div>
          </div>

          {/* Оговорка на видном месте, а не мелким шрифтом внизу. Число
              легко принять за «вероятность ИИ», а это не она: человек,
              пишущий канцеляритом, получит те же баллы заслуженно. */}
          <p className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3 text-xs leading-relaxed text-[var(--color-text-muted)]">
            {t('ne_detektor')}
          </p>

          {otchyot.shtrafy.length > 0 && (
            <>
              <h2 className="mb-3 text-sm font-semibold">{t('za_chto')}</h2>
              <ul className="mb-6 space-y-2">
                {otchyot.shtrafy.map((sh) => (
                  <li
                    key={sh.prichina}
                    className="flex items-start justify-between gap-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-2.5 text-sm"
                  >
                    <span>{sh.prichina}</span>
                    <span className="shrink-0 tabular-nums text-[var(--color-text-muted)]">
                      {sh.bally}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {otchyot.zametki.map((z) => (
            <p
              key={z}
              className="mb-6 flex items-start gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-3 text-sm leading-relaxed"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              {z}
            </p>
          ))}

          {poKategoriyam.length > 0 && (
            <>
              <h2 className="mb-3 text-sm font-semibold">{t('chto_nashli')}</h2>
              <div className="mb-6 space-y-4">
                {poKategoriyam.map(([kategoriya, nahodki]) => (
                  <div key={kategoriya}>
                    <p className="mb-1.5 text-xs font-medium text-[var(--color-accent-400)]">
                      {kategoriya}
                    </p>
                    <ul className="space-y-1">
                      {nahodki.map((n) => (
                        <li key={n.imya} className="text-sm text-[var(--color-text-muted)]">
                          {n.imya}
                          {n.kolichestvo > 1 && (
                            <span className="ml-1 tabular-nums">x{n.kolichestvo}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Пункт про канцелярит отдельно: он единственный требует
              словаря. Предупреждение о весе стоит прямо на кнопке, потому
              что мегабайт по чужому решению — это то, о чём человека
              спрашивают, а не ставят перед фактом. */}
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-4">
            <h2 className="mb-1.5 text-sm font-semibold">{t('kancelyarit_title')}</h2>
            {otchyot.morfologiya ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('kancelyarit_result', {
                  otnoshenie: otchyot.morfologiya.otnoshenie,
                  sush: otchyot.morfologiya.sushestvitelnyh,
                  glag: otchyot.morfologiya.glagolnyh,
                })}
              </p>
            ) : (
              <>
                <p className="mb-3 text-sm leading-relaxed text-[var(--color-text-muted)]">
                  {t('kancelyarit_hint')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void vzyatSlovar();
                  }}
                  disabled={gruzitsya}
                  className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium transition-colors hover:border-[var(--color-accent-500)] hover:text-[var(--color-accent-400)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {gruzitsya ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('slovar_gruzitsya')}
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      {t('slovar_zagruzit', { mb: VES_SLOVARYA_MB })}
                    </>
                  )}
                </button>
                {oshibkaSlovarya && (
                  <p className="mt-3 flex items-start gap-2 text-sm text-[var(--color-text-muted)]">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                    {t('slovar_oshibka')}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
